import { FieldSave } from 'signer-app/signer-signature-document/types';
import { unreachable, UnreachableError } from 'signer-app/utils/unreachable';
import moment from 'moment';
import {
  CheckboxField,
  ImageField,
  RadioButtonField,
  RectangleField,
  DateField,
  DropdownField,
  InitialsField,
  SignatureField,
  TextField,
  HyperlinkField,
  DateFieldFontFamilyEnum,
  TextFieldValidationTypeEnum,
  FieldJoined,
} from '@dropbox/sign-internal-client';
import { typeMap } from 'signer-app/signer-experience/use-auto-save-signer-experience';

// TODO: check if this is used
export const mergeSavedData = (
  f: FieldJoined,
  saved?: FieldSave,
): FieldJoined => {
  if (saved && saved.componentType === typeMap[f.type]) {
    switch (saved.componentType) {
      case 'inputcheckmark': {
        const field = f as CheckboxField;
        return {
          ...field,
          checked: saved.data.checked,
        };
      }
      case 'inputradiobutton': {
        const field = f as RadioButtonField;
        return {
          ...field,
          checked: saved.data.checked,
          // Radiobuttons must ALWAYS have a group. it doesn't make
          // any sense to have one without a group
          group: saved.data.group!,
        };
      }
      case 'inputtextbox': {
        const field = f as TextField;
        return {
          ...field,
          value: saved.data.content,
          lines: saved.data.content,
          fontSize: saved.data.fontSize,
          fontFamily: saved.data.fontFamily as DateFieldFontFamilyEnum,
          masked: saved.data.masked,
          validationType: saved.data
            .validationType as TextFieldValidationTypeEnum,
        };
      }
      case 'inputdropdown': {
        const field = f as DropdownField;
        return {
          ...field,
          value: saved.data.content,
          fontFamily: saved.data.fontFamily as DateFieldFontFamilyEnum,
          fontSize: saved.data.fontSize,
        };
      }
      case 'inputdate': {
        const field = f as DateField;
        return {
          ...field,
          value: saved.data.content,
          fontSize: saved.data.fontSize,
          fontFamily: saved.data.fontFamily as DateFieldFontFamilyEnum,
          dateFormat: saved.data.format,
        };
      }
      case 'inputinitials': {
        const field = f as InitialsField;
        return {
          ...field,
          signature: saved.data.imgSrc
            ? {
                ...field.signature,
                guid: saved.data.imgSrc.split('=')[1],
              }
            : undefined,

          height: saved.data.height,
          width: saved.data.width,
        };
      }
      case 'inputsignature': {
        const field = f as SignatureField;
        return {
          ...field,
          signature: saved.data.imgSrc
            ? {
                ...field.signature,
                guid: saved.data.imgSrc.split('=')[1],
              }
            : undefined,
          height: saved.data.height,
          width: saved.data.width,
        };
      }
      case 'inputimage': {
        const field = f as ImageField;
        return {
          ...field,
          src: saved.data.image!,
          height: saved.data.height,
          width: saved.data.width,
        };
      }
      case 'inputrectangle': {
        const field = f as RectangleField;
        return {
          ...field,
          height: saved.data.height,
          width: saved.data.width,
        };
      }
      case 'inputhyperlink': {
        const field = f as HyperlinkField;
        return {
          ...field,
          value: saved.data.value ?? '',
          height: saved.data.height,

          width: saved.data.width,
        };
      }
      default:
        unreachable(saved);
    }
  }
  return f;
};

export const extractSavedData = (
  field: FieldJoined,
  options?: { preserveOriginalRequired?: boolean },
): FieldSave => {
  const required = options?.preserveOriginalRequired
    ? !!(field.originalRequired ?? field.required)
    : !!field.required;

  switch (field.type) {
    case 'hyperlink':
      return {
        componentType: 'inputhyperlink',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          value: field.value ?? undefined,
        },
      };
    case 'rectangle':
      return {
        componentType: 'inputrectangle',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
        },
      };
    case 'image':
      return {
        componentType: 'inputimage',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          image: field.src,
        },
      };
    case 'date':
      return {
        componentType: 'inputdate',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          content: !field.hidden ? field.value ?? undefined : undefined,
          fontSize: field.fontSize,
          fontFamily: field.fontFamily,
          format: field.dateFormat ?? undefined,
        },
      };
    case 'dropdown':
      return {
        componentType: 'inputdropdown',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          content: !field.hidden ? field.value ?? undefined : undefined,
          fontFamily: field.fontFamily,
          fontSize: field.fontSize,
          masked: false,
        },
      };
    case 'checkbox':
      return {
        componentType: 'inputcheckmark',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          required,
          checked: !field.hidden ? !!field.checked : false,
          height: field.height,
          width: field.width,
        },
      };
    case 'radiobutton':
      return {
        componentType: 'inputradiobutton',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          required,
          checked: !field.hidden ? field.checked : false,
          group: field.group,
          height: field.height,
          width: field.width,
        },
      };
    case 'initials':
      return {
        componentType: 'inputinitials',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          imgSrc:
            !field.hidden && field.signature
              ? `/attachment/view?sig_guid=${field.signature.guid}`
              : undefined,
        },
      };
    case 'signature':
      return {
        componentType: 'inputsignature',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,

          imgSrc:
            !field.hidden && field.signature
              ? `/attachment/view?sig_guid=${field.signature.guid}`
              : undefined,
        },
      };
    case 'text':
      return {
        componentType: 'inputtextbox',
        x: field.x,
        y: field.y,
        z: 500,
        data: {
          id: field.id,
          apiId: field.apiId ?? '',
          height: field.height,
          width: field.width,
          required,
          // Multiline fields use a .lines with \n characters in it.
          content: !field.hidden
            ? (field.lines || field.value) ?? undefined
            : undefined,
          fontSize: field.fontSize,
          fontFamily: field.fontFamily,
          masked: field.masked,
          validationType: field.validationType ?? undefined,
        },
      };
    // istanbul ignore next
    default:
      throw new UnreachableError(field);
  }
};

/**
 * Populates field values that need to be set on the client.
 */
export function populateFieldValues(
  fields: Array<FieldJoined>,
  settings: {
    defaultDateFormat?: string;
  } = {},
) {
  return fields.map((field) => {
    if (field.type === 'date') {
      // set the date format that is passed in
      let dateFormat = settings?.defaultDateFormat;
      // if the field has a format specified and is not default use it
      if (field.dateFormat && field.dateFormat !== 'default') {
        dateFormat = field.dateFormat;
      }
      return {
        ...field,
        dateFormat: field.dateFormat || dateFormat,
        // For read-only fields (from previous signers), preserve the existing value
        // For editable fields (current signer), always generate fresh date
        value: field.readOnly ? field.value : moment().format(dateFormat),
      };
    } else {
      return field;
    }
  });
}
